import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import Context from '@context/store-finder';
import { getDistance, getDevice } from '@utils';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_ID } from '@constants';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import icon from '@active-channel/images/map-marker.png';
import activeIcon from '@active-channel/images/map-marker-active.png';
import gps from '../../images/current-location.png';

import './styles/styles.scss';

class Map extends Component {
  constructor(props) {
    super(props);
    this.state = {
      localMarkers: [],
    };

    this.maxDistance = 35; // in kilometers
    this.defaultZoom = 12;
    this.myLocation = null;
    this.mapRef = createRef();
  }

  componentDidMount() {
    this.loadMap();
    this.context.setReloadMapMethod(this.setCoordinates);
    this.context.setSearchMethod(this.searchMap);
    this.context.setToggleOpenMethod(this.toggleOpenStores);
  }

  /**
   * Load the required scripts for the google maps api
   */

  loadMap = () => {
    if (window.google && window.google.maps) {
      this.renderMap();
      this.context.setGoogleLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry,places,marker&v=weekly`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.google && window.google.maps) {
        this.renderMap();
        this.context.setGoogleLoaded(true);
      } else {
        console.error('Google Maps failed to load.');
      }
    };

    document.head.appendChild(script);
  };

  /**
   * Create an instance of the google maps map
   * and start to add markers
   */
  renderMap = () => {
    this.map = new google.maps.Map(this.mapRef.current, {
      zoom: 7,
      center: { lat: 52.23, lng: 4.55 },
      mapId: GOOGLE_MAPS_ID,
      mapTypeControl: false,
      fullscreenControl: false,
    });
    this.addMarkers();
  };

  /**
   * Loop trough all provided stores and create a Marker instance for each
   * Also include a (hidden) marker for myLocation and assign all markers to
   * a new MarkerClusterer instance.
   *
   * @param {*} initiate
   */
  addMarkers = (initiate = false) => {
    const { filteredStores, setStores, setActiveStore, activeStore, setTempStoreId } = this.context;
    const bounds = new google.maps.LatLngBounds();
    const updatedStores = [];
    const markers = [];

    filteredStores.forEach((location) => {
      const { latitude, longitude } = location.attributes;
      if (latitude == null || longitude == null || isNaN(latitude) || isNaN(longitude)) {
        return null;
      }

      const isActive = activeStore?.storeId === location.storeId;

      const markerElement = document.createElement('div');
      markerElement.className = 'custom-marker';
      markerElement.innerHTML = `<img src="${isActive ? activeIcon : icon}" alt="Store Marker" />`;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: new google.maps.LatLng(latitude, longitude),
        map: this.map,
        title: location.storeId,
        gmpClickable: true,
        content: markerElement,
      });
      marker.content.dataset.storeId = location.storeId;


      bounds.extend(marker.position);

      const markerMethods = {
        toggleBounce: () => this.toggleBounce(marker, false),
        deactivate: () => this.toggleBounce(marker, true),
        forceActiveState: () => marker.content.firstChild.src = activeIcon,
        checkIfOpen: () => this.checkIfOpen(marker),
        resetIcon: () => marker.content.firstChild.src = icon,
      };

      updatedStores.push({
        ...location,
        ...markerMethods,
      });

      markers.push(marker);

      marker.addListener('click', () => {
        const { isMobile } = getDevice();
        setTempStoreId(null);

        const storeId = marker.content.dataset.storeId;
        if (storeId === this.context.activeStore?.storeId) {
          setActiveStore({});
          marker.content.firstChild.src = icon;
        } else {
          const store = filteredStores.find(store => store.storeId === storeId) || {};
          setActiveStore({ ...store }, true);
          this.state.localMarkers.forEach(m => m.content.firstChild.src = icon);
          marker.content.firstChild.src = activeIcon;
        }

        if (isMobile) {
          document.querySelector('.store-location').scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      });
      marker.content.addEventListener('mouseenter', () => this.hoverMarker(marker, true));
      marker.content.addEventListener('mouseleave', () => this.hoverMarker(marker, false));
    });

    const gpsElement = document.createElement('div');
    gpsElement.className = 'gps-marker';
    gpsElement.innerHTML = `<img src="${gps}" alt="Current Location" />`;

    this.myLocation = new google.maps.marker.AdvancedMarkerElement({
      position: new google.maps.LatLng(0, 0),
      content: gpsElement,
      gmpClickable: false,
    });

    markers.push(this.myLocation);
    this.setState({ localMarkers: markers });
    setStores(updatedStores, initiate);

    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
    }

    this.markerClusterer = new MarkerClusterer({
      markers,
      map: this.map,
      renderer: {
        render: ({ count, position }) => {
          const div = document.createElement('div');
          div.className = 'cluster';
          div.innerText = count;
          return new google.maps.marker.AdvancedMarkerElement({
            position,
            content: div,
          });
        },
      },
    });

    if (markers.length > 0) {
      this.map.fitBounds(bounds);
    } else {
      this.map.setZoom(this.defaultZoom);
    }
  };

  /**
   * [Context method] Use the google api to get coordinates for a query
   * and show the results on the map (or not)
   * @param {string} query
   */
  searchMap = (query) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({
      address: query,
      componentRestrictions: {
        country: 'NL',
      },
    }, (results, status) => {
      if (status === 'OK') {
        this.setCoordinates({
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        });
      } else {
        this.context.showNoResults();
      }
      this.context.setSearching(false);
    });
  };

  /**
   * Update the map with new coordinates
   * @param {object} coords
   * @param {boolean} gps
   */
  setCoordinates = (coords, gps) => {
    const { stores, setFilteredStores, allStores, setAllStores } = this.context;
    const nearbyMarkers = this.findNearbyLocations(coords);
    const filteredStores = nearbyMarkers.map((marker) => {
      const storeId = marker.content.dataset.storeId;
      const store = stores.find(store => store.storeId === storeId);
      return {
        ...marker,
        ...store,
      };
    });
    this.map.setZoom(this.defaultZoom);
    const position = new google.maps.LatLng(coords.lat, coords.lng);
    this.map.setCenter(position);
    this.myLocation.position = gps ? { lat: coords.lat, lng: coords.lng } : { lat: 0, lng: 0 };
    this.myLocation.map = gps ? this.map : null;

    const updatedStores = allStores.map((store) => {
      const marker = nearbyMarkers.find(marker => store.storeId === marker.content.dataset.storeId) || {};
      return {
        ...store,
        ...marker,
      };
    });
    setAllStores(updatedStores);
    setFilteredStores(filteredStores, true);
    if (!filteredStores.length) {
      const closestMarker = this.findClosest(coords);
      const store = stores.find(store => store.storeId === closestMarker.storeId) || {};
      const closestStore = store.storeId ? {
        ...closestMarker,
        ...store,
      } : undefined;

      this.context.showNoResults(closestStore);
    }
  };

  toggleOpenStores = (open) => {
    this.state.localMarkers.forEach(marker => {
      marker.setMap(open ? this.map : null);
    });
  };

  /**
   * Find the closest store (one) to a set of coordinates
   * can either be for gps locations or any coords
   * @param {object} coords
   */
  findClosest = (coords) => {
    const { filteredStores } = this.context;
    return filteredStores
      .map(store => ({
        ...store,
        distance: getDistance(coords.lat, coords.lng, store.marker?.position.lat, store.marker?.position.lng),
      }))
      .reduce((closest, store) => (store.distance < closest.distance ? store : closest), filteredStores[0]);
  };

  /**
   * Find all stores in the vicinity of the provided coords
   * Filter them by max distance and sort from close to far
   * @param {object} coords
   */
  findNearbyLocations = (coords) => {
    const sortedByDistance = this.state.localMarkers.map((marker) => {
      marker.distance = getDistance({ from: coords, to: { lat: marker.position.lat, lng: marker.position.lng } });
      return marker;
    })
      .filter((marker) => {
        return marker.distance < this.maxDistance;
      })
      .sort((a, b) => (a.distance >= b.distance ? 1 : -1));
    return sortedByDistance;
  };

  /**
   * Event listener action to change the icon of the marker on hover
   * @param {object} marker
   * @param {boolean} active
   */
  hoverMarker(marker, active) {
    const storeId = marker.content.dataset.storeId;
    const { activeStore, setTempStoreId, tempStoreId } = this.context;
    if (marker.isGPS || (activeStore?.storeId && activeStore.storeId === storeId)) {
      return;
    }
    setTempStoreId(marker.content.dataset.storeId === tempStoreId ? null : storeId);
    marker.content.firstChild.src = active ? activeIcon : icon;
  }

  toggleBounce(marker, cancel) {
    const { activeStore } = this.context;
    if (activeStore?.storeId === marker.content.dataset.storeId) {
      return;
    }
    if (cancel) {
      marker.content.classList.remove('bounce');
      return;
    }
    marker.content.classList.add('bounce');
  }

  /**
   * TODO: BOOKDEV-7567
   * @param {boolean} open
   */
  toggleOpenStores = (open) => {
    this.map.setZoom(this.map.zoom - 1);
    this.state.localMarkers.forEach((marker) => {
      const isOpen = this.checkIfOpen(marker);
      marker.setVisible(open ? isOpen : true);
    });
    this.map.setZoom(this.map.zoom + 1);
  };

  /**
   * TODO: BOOKDEV-7567
   * @param {boolean} open
   */
  checkIfOpen = (marker) => {
    const { openMonday, openTuesday, openWednesday, openThursday, openFriday, openSaturday, openSunday } = marker.attributes || {};
    const week = [openSunday, openMonday, openTuesday, openWednesday, openThursday, openFriday, openSaturday];

    const date = new Date();
    const openingHours = week[date.getDay()] || '';
    const [openingTime, closingTime] = openingHours.match(/\d{2}(\.|\:)\d{2}/g) || [];
    const currentHour = date.getHours();

    if (!openingTime || !closingTime) {
      return false;
    }

    return currentHour >= parseInt(openingTime) && currentHour < parseInt(closingTime);
  };

  render() {
    const { show = true } = this.props;
    const { isMobile } = getDevice();
    return (
      <div
        ref={this.mapRef}
        className={`google-map ${show ? 'show' : 'hidden'}`}
        style={{ height: isMobile ? '50vh' : `100%` }}
      />
    );
  }
}

Map.contextType = Context;
Map.propTypes = {
  store: PropTypes.object,
  height: PropTypes.number,
  show: PropTypes.bool,
};

export default Map;
